import { useActionConfirmation } from '../components/ActionFeedbackProvider';
import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Eye, EyeOff, Pencil, ShieldCheck, Trash2, UserPlus, Users, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import PageHeading from '../components/PageHeading';

const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{6,12}$/;

const AdminUsersScreen = () => {
  const { t } = useTranslation();
  const confirmAction = useActionConfirmation();

  const navigate = useNavigate();
  const auth = JSON.parse(localStorage.getItem('auth') || '{}');
  const isAdmin = String(auth?.role || '').toUpperCase() === 'ADMIN';
  const [users, setUsers] = useState([]);
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('USER');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editUsername, setEditUsername] = useState('');
  const [editRole, setEditRole] = useState('USER');
  const [editPassword, setEditPassword] = useState('');
  const [editConfirmPassword, setEditConfirmPassword] = useState('');
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showEditConfirmPassword, setShowEditConfirmPassword] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/users');
      setUsers(Array.isArray(response.data) ? response.data : []);
      setError('');
    } catch (loadError) {
      setError(loadError?.response?.status === 403 ? t('adminOnly') : t('usersLoadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (isAdmin) loadUsers();
    else setLoading(false);
  }, [isAdmin, loadUsers]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    const normalizedUsername = username.trim();
    if (normalizedUsername.length < 3 || normalizedUsername.length > 40) {
      setError(t('usernameLengthError'));
      return;
    }
    if (!PASSWORD_PATTERN.test(password)) {
      setError(t('passwordInvalid'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('passwordMismatch'));
      return;
    }

    setSaving(true);
    try {
      await api.post('/users', { username: normalizedUsername, password, role });
      setUsername('');
      setPassword('');
      setConfirmPassword('');
      setRole('USER');
      setSuccess(t('userCreatedSuccess'));
      await loadUsers();
    } catch (createError) {
      setError(createError?.response?.status === 409 ? t('usernameExists') : createError?.response?.data?.message || t('userCreateError'));
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (user) => {
    setEditingUser(user);
    setEditUsername(user.username);
    setEditRole(user.role);
    setEditPassword('');
    setEditConfirmPassword('');
    setShowEditPassword(false);
    setShowEditConfirmPassword(false);
    setError('');
    setSuccess('');
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    const isEditingCurrentUser = editingUser?.username === auth.username;
    const normalizedUsername = editUsername.trim();
    if (!isEditingCurrentUser && (normalizedUsername.length < 3 || normalizedUsername.length > 40)) {
      setError(t('usernameLengthError'));
      return;
    }
    if ((!isEditingCurrentUser && editPassword) || isEditingCurrentUser) {
      if (!PASSWORD_PATTERN.test(editPassword)) {
        setError(t('passwordInvalid'));
        return;
      }
    }
    if (editPassword !== editConfirmPassword) {
      setError(t('passwordMismatch'));
      return;
    }
    setEditSaving(true);
    try {
      await api.put(`/users/${editingUser.id || editingUser._id}`, {
        username: isEditingCurrentUser ? editingUser.username : normalizedUsername,
        role: isEditingCurrentUser ? editingUser.role : editRole,
        password: editPassword,
      });
      if (isEditingCurrentUser) {
        localStorage.removeItem('auth');
        navigate('/login', { replace: true });
        return;
      }
      setEditingUser(null);
      setSuccess(t('userUpdatedSuccess'));
      await loadUsers();
    } catch (updateError) {
      const serverMessage = updateError?.response?.data?.message;
      setError(updateError?.response?.status === 409
        ? (serverMessage === 'Username already exists' ? t('usernameExists') : t('protectedAdminAction'))
        : serverMessage || t('userUpdateError'));
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (user) => {
    const confirmed = await confirmAction(t('confirmDeleteUser', { username: user.username }));
    if (!confirmed) return;

    const userId = user.id || user._id;
    setDeletingUserId(userId);
    setError('');
    setSuccess('');
    try {
      await api.delete(`/users/${userId}`);
      setSuccess(t('userDeletedSuccess'));
      await loadUsers();
    } catch (deleteError) {
      setError(deleteError?.response?.status === 409 ? t('protectedAdminAction') : t('userDeleteError'));
    } finally {
      setDeletingUserId('');
    }
  };

  if (!isAdmin) {
    return <div className="mx-auto max-w-6xl rounded-xl border border-red-200 bg-white p-8 text-center shadow-md dark:border-red-900 dark:bg-gray-800">{t('adminOnly')}</div>;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 py-6 px-4 sm:px-6 md:px-8">
      <PageHeading icon={<ShieldCheck size={26} />} title={t('adminUsers')} />

      <section className="overflow-hidden rounded-xl border bg-gray-100 shadow-md dark:bg-gray-700">
        <div className="flex items-center gap-3 px-6 py-4">
          <UserPlus size={20} />
          <h2 className="text-lg font-bold uppercase tracking-wide">{t('createUser')}</h2>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 bg-white p-5 dark:bg-gray-800 sm:p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <label className="space-y-1 text-sm font-medium">
              <span>{t('username')}</span>
              <input autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} required minLength={3} maxLength={40} disabled={saving} className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
            </label>
            <label className="space-y-1 text-sm font-medium">
              <span>{t('password')}</span>
              <span className="relative block">
                <input type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} required disabled={saving} className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 pr-11 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
                <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? t('hidePassword') : t('showPassword')} className="absolute inset-y-0 right-3 text-gray-500">{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
              </span>
            </label>
            <label className="space-y-1 text-sm font-medium">
              <span>{t('confirmPassword')}</span>
              <span className="relative block">
                <input type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required disabled={saving} className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 pr-11 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
                <button type="button" onClick={() => setShowConfirmPassword((value) => !value)} aria-label={showConfirmPassword ? t('hidePassword') : t('showPassword')} className="absolute inset-y-0 right-3 text-gray-500">{showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
              </span>
            </label>
            <label className="space-y-1 text-sm font-medium">
              <span>{t('role')}</span>
              <select value={role} onChange={(event) => setRole(event.target.value)} disabled={saving} className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white">
                <option value="USER">{t('standardUser')}</option>
                <option value="ADMIN">{t('administrator')}</option>
              </select>
            </label>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('passwordRequirements')}</p>
          {error && <p role="alert" className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300"><AlertCircle size={18} />{error}</p>}
          {success && <p role="status" className="flex items-center gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-700 dark:bg-green-950/40 dark:text-green-300"><CheckCircle2 size={18} />{success}</p>}
          <div className="flex justify-end">
            <button type="submit" disabled={saving} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-gray-900 px-5 py-2 font-semibold text-white transition hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"><UserPlus size={18} />{saving ? t('creatingUser') : t('createUser')}</button>
          </div>
        </form>
      </section>

      <section className="overflow-hidden rounded-xl border bg-gray-100 shadow-md dark:bg-gray-700">
        <div className="flex items-center gap-3 px-6 py-4"><Users size={20} /><h2 className="text-lg font-bold uppercase tracking-wide">{t('registeredUsers')}</h2></div>
        <div className="bg-white p-5 dark:bg-gray-800 sm:p-6">
          {error && !editingUser && <p role="alert" className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300"><AlertCircle size={18} />{error}</p>}
          {success && <p role="status" className="mb-4 flex items-center gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-700 dark:bg-green-950/40 dark:text-green-300"><CheckCircle2 size={18} />{success}</p>}
          {loading ? <p className="py-4 text-center text-gray-500">{t('loading')}</p> : users.length === 0 ? <p className="py-4 text-center text-gray-500">{t('noUsers')}</p> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[620px] text-left text-sm">
                <thead className="border-b text-xs uppercase text-gray-500 dark:border-gray-700"><tr><th className="px-4 py-3">{t('username')}</th><th className="px-4 py-3">{t('role')}</th><th className="px-4 py-3 text-right">{t('actions')}</th></tr></thead>
                <tbody>{users.map((user) => {
                  const userId = user.id || user._id || user.username;
                  const isCurrentUser = user.username === auth.username;
                  return <tr key={userId} className="border-b last:border-0 dark:border-gray-700">
                    <td className="px-4 py-3 font-medium">{user.username}{isCurrentUser && <span className="ml-2 text-xs text-gray-500">({t('currentUser')})</span>}</td>
                    <td className="px-4 py-3">{user.role === 'ADMIN' ? t('administrator') : t('standardUser')}</td>
                    <td className="px-4 py-3"><div className="flex justify-end gap-2">
                      <button type="button" onClick={() => startEditing(user)} disabled={Boolean(deletingUserId)} title={isCurrentUser ? t('editOwnPassword') : t('editUser')} aria-label={`${isCurrentUser ? t('editOwnPassword') : t('editUser')}: ${user.username}`} className="rounded-lg border border-gray-300 p-2 text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"><Pencil size={16} /></button>
                      <button type="button" onClick={() => handleDelete(user)} disabled={isCurrentUser || Boolean(deletingUserId)} title={isCurrentUser ? t('protectedAdminAction') : t('deleteUser')} aria-label={`${t('deleteUser')}: ${user.username}`} className="rounded-lg border border-red-200 p-2 text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/40">{deletingUserId === userId ? <span className="block h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent" /> : <Trash2 size={16} />}</button>
                    </div></td>
                  </tr>;
                })}</tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {editingUser && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="presentation">
        <section role="dialog" aria-modal="true" aria-labelledby="edit-user-title" className="w-full max-w-xl overflow-hidden rounded-xl border bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center justify-between bg-gray-100 px-5 py-4 dark:bg-gray-700">
            <h2 id="edit-user-title" className="text-lg font-bold uppercase">{editingUser.username === auth.username ? t('changePasswordFor', { username: editingUser.username }) : `${t('editUser')}: ${editingUser.username}`}</h2>
            <button type="button" onClick={() => setEditingUser(null)} aria-label={t('close')} className="rounded p-1 hover:bg-gray-200 dark:hover:bg-gray-600"><X size={20} /></button>
          </div>
          <form onSubmit={handleEditSubmit} className="space-y-4 p-5 sm:p-6">
            {editingUser.username !== auth.username && <>
              <label className="block space-y-1 text-sm font-medium"><span>{t('username')}</span><input value={editUsername} onChange={(event) => setEditUsername(event.target.value)} required minLength={3} maxLength={40} disabled={editSaving} className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white" /></label>
              <label className="block space-y-1 text-sm font-medium"><span>{t('role')}</span><select value={editRole} onChange={(event) => setEditRole(event.target.value)} disabled={editSaving} className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"><option value="USER">{t('standardUser')}</option><option value="ADMIN">{t('administrator')}</option></select></label>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('leavePasswordBlank')}</p>
            </>}
            <label className="block space-y-1 text-sm font-medium"><span>{t('newPassword')}</span><span className="relative block"><input type={showEditPassword ? 'text' : 'password'} autoComplete="new-password" value={editPassword} onChange={(event) => setEditPassword(event.target.value)} disabled={editSaving} className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 pr-11 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white" /><button type="button" onClick={() => setShowEditPassword((visible) => !visible)} aria-label={showEditPassword ? t('hidePassword') : t('showPassword')} className="absolute inset-y-0 right-3 text-gray-500">{showEditPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></span></label>
            <label className="block space-y-1 text-sm font-medium"><span>{t('confirmNewPassword')}</span><span className="relative block"><input type={showEditConfirmPassword ? 'text' : 'password'} autoComplete="new-password" value={editConfirmPassword} onChange={(event) => setEditConfirmPassword(event.target.value)} disabled={editSaving} className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 pr-11 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white" /><button type="button" onClick={() => setShowEditConfirmPassword((visible) => !visible)} aria-label={showEditConfirmPassword ? t('hidePassword') : t('showPassword')} className="absolute inset-y-0 right-3 text-gray-500">{showEditConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></span></label>
            {editPassword && <p className="text-sm text-gray-500 dark:text-gray-400">{t('passwordRequirements')}</p>}
            {error && <p role="alert" className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300"><AlertCircle size={18} />{error}</p>}
            <div className="flex justify-end gap-3 pt-2"><button type="button" onClick={() => setEditingUser(null)} disabled={editSaving} className="rounded-lg border border-gray-300 px-4 py-2 font-semibold hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-700">{t('cancel')}</button><button type="submit" disabled={editSaving} className="rounded-lg bg-gray-900 px-4 py-2 font-semibold text-white hover:bg-gray-700 disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200">{editSaving ? t('saving') : editingUser.username === auth.username ? t('changePassword') : t('save')}</button></div>
          </form>
        </section>
      </div>}
    </div>
  );
};

export default AdminUsersScreen;
